import type {
  KYCQuestionDefinition,
  KYCQuestionRule,
  KYCQuestionnaire,
  KYCQuestionnaireElement,
} from '@anchorage/graphql/generated';
import {
  KYCQuestionRuleType,
  KYCQuestionType,
  KYCQuestionnaireElementType,
} from '@anchorage/graphql/generated';
import { formatCurrentDate } from '@anchorage/utils';
import { yup } from '@latitude/form';

import {
  getQuestionPostfixFromType,
  questionHasValue,
  showElementBasedOnAnsweredOptions,
} from '../hooks/useQuestionnaire/utils';

/**
 * Yup context read by the questionnaire schema. It is derived from the form
 * values on every validation run by createQuestionnaireResolver.
 */
export type QuestionnaireValidationContext = {
  /** Every answered option id mapped to the id of the question that answered it */
  answeredOptions: Record<string, string>;
  /** Top-level questions hidden by an unmet trigger. All their rules are skipped */
  hiddenQuestionIDs: string[];
};

/**
 * Creates a Yup string schema from backend validation rules.
 * Supports: REQUIRED, REGEX, MIN_LENGTH, MAX_LENGTH, MIN_DATE, MAX_DATE.
 *
 * Date validation supports:
 * - Fixed date strings (e.g., "2024-12-31")
 * - "TODAY" keyword for current date
 * - Cross-field dependencies via questionId (requires context)
 *
 * Optional fields:
 * - When isRequired is false, the schema allows null and undefined values
 *
 * @param rules - Array of KYC question validation rules from the backend
 * @param isRequired - Whether the field is required
 * @returns A Yup string schema with the appropriate validations
 */
export const createYupSchemaFromQuestion = (
  rules: KYCQuestionRule[] | undefined,
  isRequired: boolean,
): yup.StringSchema<string | null | undefined> => {
  let schema: yup.StringSchema<string | null | undefined> = yup.string();

  // Handle required field
  if (isRequired) {
    schema = schema
      .required('Field required')
      .matches(/^(?!\s*$).+/, 'Field required') as yup.StringSchema<
      string | null | undefined
    >; // No empty spaces allowed
  } else {
    // For optional fields, allow null and undefined values
    schema = schema.nullable();
  }

  // Apply validation rules from backend
  if (rules) {
    for (const rule of rules) {
      switch (rule.type) {
        case KYCQuestionRuleType.REGEX:
          schema = schema.matches(
            new RegExp(rule.value),
            rule.errorMessage || 'Invalid format',
          );
          break;

        case KYCQuestionRuleType.MIN_LENGTH:
          schema = schema.min(
            Number.parseInt(rule.value, 10),
            rule.errorMessage || `Minimum length is ${rule.value}`,
          );
          break;

        case KYCQuestionRuleType.MAX_LENGTH:
          schema = schema.max(
            Number.parseInt(rule.value, 10),
            rule.errorMessage || `Maximum length is ${rule.value}`,
          );
          break;

        case KYCQuestionRuleType.MIN_DATE: {
          const errorMessage =
            rule.errorMessage || `Date must be on or after ${rule.value}`;

          schema = schema.test('minDate', errorMessage, function (value) {
            if (!value) {
              return true; // Let required validation handle empty values
            }

            // Resolve the date value
            let dateValue = rule.value;

            // If questionId is provided, try to get the date from that field
            if (rule.questionId && this.options.context) {
              const contextValue = (
                this.options.context as Record<string, unknown>
              )[rule.questionId];
              if (contextValue && typeof contextValue === 'object') {
                const freeformText = (contextValue as Record<string, unknown>)
                  .freeformText;
                if (typeof freeformText === 'string') {
                  dateValue = freeformText;
                }
              }
            }

            if (!dateValue) {
              return true; // No min date to validate against
            }

            // Normalize dates to midnight UTC for comparison
            const minDate =
              dateValue === 'TODAY'
                ? new Date(formatCurrentDate('businessDays'))
                : new Date(dateValue as string);
            const inputDate = new Date(value as string);

            // Set both dates to midnight for date-only comparison
            minDate.setUTCHours(0, 0, 0, 0);
            inputDate.setUTCHours(0, 0, 0, 0);

            return inputDate >= minDate;
          });
          break;
        }

        case KYCQuestionRuleType.MAX_DATE: {
          const errorMessage =
            rule.errorMessage || `Date must be on or before ${rule.value}`;

          schema = schema.test('maxDate', errorMessage, function (value) {
            if (!value) {
              return true; // Let required validation handle empty values
            }

            // Resolve the date value
            let dateValue = rule.value;

            // If questionId is provided, try to get the date from that field
            if (rule.questionId && this.options.context) {
              const contextValue = (
                this.options.context as Record<string, unknown>
              )[rule.questionId];
              if (contextValue && typeof contextValue === 'object') {
                const freeformText = (contextValue as Record<string, unknown>)
                  .freeformText;
                if (typeof freeformText === 'string') {
                  dateValue = freeformText;
                }
              }
            }

            if (!dateValue) {
              return true; // No max date to validate against
            }

            // Normalize dates to midnight UTC for comparison
            const maxDate =
              dateValue === 'TODAY'
                ? new Date(formatCurrentDate('businessDays'))
                : new Date(dateValue as string);
            const inputDate = new Date(value as string);

            // Set both dates to midnight for date-only comparison
            maxDate.setUTCHours(0, 0, 0, 0);
            inputDate.setUTCHours(0, 0, 0, 0);

            return inputDate <= maxDate;
          });
          break;
        }

        // Note: Complex rules (DISTINCT, MAX_SUM) are handled by createGroupArraySchema
        default:
          break;
      }
    }
  }

  return schema;
};

/**
 * Configuration for group array validation with MAX_SUM or DISTINCT rules
 */
export type GroupFieldConfig = {
  /** The field path within each item to validate (e.g., 'ownership.freeformText') */
  fieldPath: string;
  /** Array of validation rules to apply at the group level for this field */
  groupRules: KYCQuestionRule[];
};

export type GroupValidationConfig = {
  /** Schema for individual items in the array */
  itemSchema: yup.ObjectSchema<Record<string, unknown>>;
  /**
   * Multiple fields with group-level rules.
   */
  fields?: GroupFieldConfig[];
};

/**
 * Creates a Yup array schema with group-level validations (MAX_SUM, DISTINCT).
 *
 * This function handles array-level validation that needs to validate across
 * all items in a group. Each field in `config.fields` is validated independently:
 * - MAX_SUM: Ensures the sum of the field across all items doesn't exceed a maximum
 * - DISTINCT: Ensures all values of the field are unique within the group
 *
 * Multiple fields can each carry their own rules, and all are enforced.
 *
 * @param config - Configuration for the group validation
 * @returns A Yup array schema with group-level validations applied
 *
 * @example
 * ```ts
 * const schema = createGroupArraySchema({
 *   fields: [
 *     {
 *       fieldPath: 'ownership.freeformText',
 *       groupRules: [{
 *         type: KYCQuestionRuleType.MAX_SUM,
 *         value: '100',
 *         errorMessage: 'Total ownership must equal 100%'
 *       }]
 *     },
 *     {
 *       fieldPath: 'email.freeformText',
 *       groupRules: [{
 *         type: KYCQuestionRuleType.DISTINCT,
 *         value: '',
 *         errorMessage: 'Emails must be unique'
 *       }]
 *     }
 *   ],
 *   itemSchema: yup.object({
 *     ownership: yup.object({ freeformText: yup.string() }),
 *     email: yup.object({ freeformText: yup.string() }),
 *   }),
 * });
 * ```
 */
export const createGroupArraySchema = (config: GroupValidationConfig) => {
  let schema = yup.array().of(config.itemSchema).defined();

  const fieldConfigs: GroupFieldConfig[] = config.fields ?? [];

  if (fieldConfigs.length === 0) {
    return schema;
  }

  // Apply group-level validation rules for each field
  for (const fieldConfig of fieldConfigs) {
    for (const rule of fieldConfig.groupRules) {
      switch (rule.type) {
        case KYCQuestionRuleType.MAX_SUM: {
          const maxValue = Number.parseFloat(rule.value);
          const errorMessage =
            rule.errorMessage || `Sum cannot exceed ${maxValue}`;

          schema = schema.test('maxSum', errorMessage, function (items) {
            if (!items || items.length === 0) {
              return true;
            }

            // Calculate sum across all items
            const sum = items.reduce((acc, item) => {
              // Navigate the field path to get the value
              const pathParts = fieldConfig.fieldPath?.split('.') ?? [];
              let value: unknown = item;

              // Traverse the object to get to the desired field
              for (const part of pathParts) {
                if (value && typeof value === 'object' && part in value) {
                  value = (value as Record<string, unknown>)[part];
                } else {
                  value = undefined;
                  break;
                }
              }

              const numValue = Number(value);
              return acc + (Number.isNaN(numValue) ? 0 : numValue);
            }, 0);

            // If sum exceeds max, create errors for all items inside the array
            if (sum > maxValue) {
              const errors: yup.ValidationError[] = [];

              for (let index = 0; index < items.length; index++) {
                // Create error for each item in the array
                errors.push(
                  this.createError({
                    message: errorMessage,
                    path: `${this.path}[${index}].${fieldConfig.fieldPath}`,
                  }),
                );
              }

              // Return a new ValidationError with the custom message and inner errors
              const error = new yup.ValidationError(
                errorMessage,
                undefined,
                this.path,
              );
              error.inner = errors;
              return error;
            }

            return true;
          });
          break;
        }

        case KYCQuestionRuleType.DISTINCT: {
          const errorMessage = rule.errorMessage || 'Values must be unique';

          schema = schema.test('distinct', errorMessage, function (items) {
            if (!items || items.length === 0) {
              return true;
            }

            // Extract values and track which indices have which values
            const valueMap = new Map<unknown, number[]>();

            for (let index = 0; index < items.length; index++) {
              const item = items[index];
              const pathParts = fieldConfig.fieldPath.split('.');
              let value: unknown = item;

              // Traverse the object to get to the desired field
              for (const part of pathParts) {
                if (value && typeof value === 'object' && part in value) {
                  value = (value as Record<string, unknown>)[part];
                } else {
                  value = undefined;
                  break;
                }
              }

              // Only track non-empty values
              if (value !== undefined && value !== null && value !== '') {
                const indices = valueMap.get(value);
                if (indices) {
                  indices.push(index);
                } else {
                  valueMap.set(value, [index]);
                }
              }
            }

            // Find duplicate values
            const duplicateIndices = new Set<number>();
            for (const [, indices] of valueMap) {
              if (indices.length > 1) {
                indices.forEach((idx) => duplicateIndices.add(idx));
              }
            }

            // If there are duplicates, create errors for each duplicate field
            if (duplicateIndices.size > 0) {
              const errors: yup.ValidationError[] = [];

              for (const index of duplicateIndices) {
                errors.push(
                  this.createError({
                    message: errorMessage,
                    path: `${this.path}[${index}].${fieldConfig.fieldPath}`,
                  }),
                );
              }

              // Return a new ValidationError with the custom message and inner errors
              const error = new yup.ValidationError(
                errorMessage,
                undefined,
                this.path,
              );
              error.inner = errors;
              return error;
            }

            return true;
          });
          break;
        }

        default:
          break;
      }
    }
  }

  return schema;
};

/**
 * Extracts rules that apply at the group level (DISTINCT, MAX_SUM)
 */
const extractGroupLevelRules = (
  rules: KYCQuestionRule[] | undefined,
): KYCQuestionRule[] => {
  if (!rules) {
    return [];
  }
  return rules.filter(
    (rule) =>
      rule.type === KYCQuestionRuleType.DISTINCT ||
      rule.type === KYCQuestionRuleType.MAX_SUM,
  );
};

/**
 * Extracts rules that apply at the question level (all except DISTINCT, MAX_SUM)
 */
const extractQuestionLevelRules = (
  rules: KYCQuestionRule[] | undefined,
): KYCQuestionRule[] => {
  if (!rules) {
    return [];
  }
  return rules.filter(
    (rule) =>
      rule.type !== KYCQuestionRuleType.DISTINCT &&
      rule.type !== KYCQuestionRuleType.MAX_SUM,
  );
};

/**
 * Creates the answer schema for a question based on its type and rules.
 * Returns the schema shape for the answer object (freeformText, options, file).
 *
 * Most questions follow the pattern:
 * {
 *   freeformText?: string,
 *   options?: string[]
 * }
 */
const createAnswerSchema = (
  questionType: KYCQuestionType,
  isRequired: boolean,
  questionLevelRules: KYCQuestionRule[],
): Record<string, yup.Schema> => {
  const schemaShape: Record<string, yup.Schema> = {};

  // Most questions use freeformText for their answer
  const usesFreeformText = [
    KYCQuestionType.TEXTFIELD,
    KYCQuestionType.TEXT_AREA,
    KYCQuestionType.DATE,
    KYCQuestionType.TAX_CERTIFICATION,
  ].includes(questionType);

  // Some questions use options array (multi-select)
  const usesOptionsArray = [
    KYCQuestionType.CHECKBOX,
    KYCQuestionType.DROPDOWN_MULTI_SELECT,
    KYCQuestionType.CHIP,
  ].includes(questionType);

  // Some questions use options as a single string (single-select)
  const usesOptionsSingle = [
    KYCQuestionType.RADIO,
    KYCQuestionType.RADIO_GROUP,
    KYCQuestionType.CARD_SELECT,
    KYCQuestionType.DROPDOWN,
  ].includes(questionType);

  // UPLOAD type uses both freeformText (for the digest) and file for the actual file
  const usesFile = [KYCQuestionType.UPLOAD].includes(questionType);

  if (usesFreeformText) {
    schemaShape.freeformText = createYupSchemaFromQuestion(
      questionLevelRules,
      isRequired,
    );
  }

  if (usesOptionsArray) {
    if (isRequired) {
      schemaShape.options = yup
        .array()
        .of(yup.string().defined())
        .min(1, 'Field required')
        .defined('Field required');
    } else {
      schemaShape.options = yup
        .array()
        .of(yup.string().defined())
        .optional()
        .nullable();
    }
  }

  if (usesOptionsSingle) {
    if (isRequired) {
      schemaShape.options = yup.string().required('Field required');
    } else {
      schemaShape.options = yup.string().optional().nullable();
    }
  }

  if (usesFile) {
    if (isRequired) {
      schemaShape.file = yup.mixed().required('Field required');
      schemaShape.freeformText = createYupSchemaFromQuestion(
        questionLevelRules,
        isRequired,
      );
    } else {
      schemaShape.freeformText = yup.string().optional().nullable();
    }
  }

  return schemaShape;
};

/**
 * Creates a schema for a single question based on its type.
 * Returns a Yup schema for the question's answer structure.
 */
const createQuestionSchema = (
  question: KYCQuestionDefinition,
): yup.ObjectSchema<Record<string, unknown>> => {
  const isRequired = !question.optional;
  const questionLevelRules = extractQuestionLevelRules(question.rules);

  // For GROUP type questions, handle separately with array validation
  if (question.type === KYCQuestionType.GROUP) {
    return createGroupQuestionSchema(question);
  }

  const schemaShape = createAnswerSchema(
    question.type,
    isRequired,
    questionLevelRules,
  );

  return yup.object(schemaShape).defined();
};

/**
 * Creates a schema for a GROUP type question.
 * Groups contain an array of answers, where each item is an object with nested question answers.
 *
 * Structure:
 * {
 *   group_answers: [
 *     {
 *       [nestedQuestionId]: { freeformText: "...", options: [...] },
 *       ...
 *     }
 *   ]
 * }
 */
const createGroupQuestionSchema = (
  question: KYCQuestionDefinition,
): yup.ObjectSchema<Record<string, unknown>> => {
  const nestedQuestions = question.group || [];

  // Build schema for each nested question in the group
  // For nested questions, we need to extract only question-level rules (not DISTINCT/MAX_SUM)
  const itemSchemaShape: Record<string, yup.Schema> = {};

  for (const nestedQuestion of nestedQuestions) {
    // For nested questions in groups, we need to apply only non-group-level rules
    // Group-level rules (DISTINCT, MAX_SUM) will be applied to the array itself
    const questionLevelRules = extractQuestionLevelRules(nestedQuestion.rules);
    // group questions requirements are handled below within the test
    const isRequired = false;

    const schemaShape = createAnswerSchema(
      nestedQuestion.type,
      isRequired,
      questionLevelRules,
    );

    itemSchemaShape[nestedQuestion.id] = yup
      .object(schemaShape)
      .test(function (value) {
        const { path, options } = this;
        // The row index is the number after `group_answers` in the path.
        // Question ids can contain digits, so the first number of the whole
        // path is not the index.
        const index = Number.parseInt(
          path?.match?.(/group_answers[.[](\d+)/)?.[1] || '0',
          10,
        );
        const answeredOptions = options?.context?.answeredOptions as
          | Record<string, string>
          | undefined;

        const hasTrigger = !!nestedQuestion.triggerOptionID?.length;
        const hasTriggerAnswer =
          hasTrigger &&
          showElementBasedOnAnsweredOptions({
            triggerOptionID: nestedQuestion.triggerOptionID,
            answeredOptions,
            groupNameAndIndex: `${question.id}.group_answers.${index}`,
          });
        const valueExists = !!questionHasValue(nestedQuestion, value);
        const fieldPostfix = getQuestionPostfixFromType(nestedQuestion.type);

        if (!hasTrigger && !valueExists && !nestedQuestion.optional) {
          return this.createError({
            path: `${path}.${fieldPostfix}`,
            message: `Field required`,
          });
        }
        if (!answeredOptions) {
          return true;
        }
        if (hasTriggerAnswer && !valueExists && !nestedQuestion.optional) {
          return this.createError({
            path: `${path}.${fieldPostfix}`,
            message: `Field required`,
          });
        }

        return true;
      });
  }

  const itemSchema = yup.object(itemSchemaShape);

  // Collect group-level rules from all nested questions that have them
  const groupFieldConfigs: GroupFieldConfig[] = [];

  for (const nestedQuestion of nestedQuestions) {
    const groupRules = extractGroupLevelRules(nestedQuestion.rules);

    if (groupRules.length > 0) {
      // The field path for group validation is [questionId].freeformText
      groupFieldConfigs.push({
        fieldPath: `${nestedQuestion.id}.freeformText`,
        groupRules,
      });
    }
  }

  // Create the array schema with group-level validations
  let arraySchema;

  if (groupFieldConfigs.length > 0) {
    arraySchema = createGroupArraySchema({
      fields: groupFieldConfigs,
      itemSchema,
    });
  } else {
    arraySchema = yup.array().of(itemSchema).defined();
  }

  // Handle min/max length for the group array
  const questionLevelRules = extractQuestionLevelRules(question.rules);
  for (const rule of questionLevelRules) {
    if (rule.type === KYCQuestionRuleType.MIN_LENGTH) {
      const minValue = Number.parseInt(rule.value, 10);
      arraySchema = arraySchema.min(
        minValue,
        rule.errorMessage || `At least ${minValue} item(s) required`,
      );
    }
    if (rule.type === KYCQuestionRuleType.MAX_LENGTH) {
      const maxValue = Number.parseInt(rule.value, 10);
      arraySchema = arraySchema.max(
        maxValue,
        rule.errorMessage || `Maximum ${maxValue} item(s) allowed`,
      );
    }
  }

  return yup.object({
    group_answers: arraySchema,
  });
};

/**
 * Skips every rule of a question while the yup context lists it as hidden.
 * Without context (no `hiddenQuestionIDs`) the question is validated normally.
 */
const skipWhenHidden = (
  questionId: string,
  schema: yup.ObjectSchema<Record<string, unknown>>,
) =>
  schema.when('$hiddenQuestionIDs', ([hiddenQuestionIDs], questionSchema) =>
    Array.isArray(hiddenQuestionIDs) && hiddenQuestionIDs.includes(questionId)
      ? yup.mixed().nullable().notRequired()
      : questionSchema,
  );

/**
 * Recursively processes questionnaire elements and builds schema shape
 */
const processQuestionnaireElement = (
  element: KYCQuestionnaireElement,
  schemaShape: Record<string, yup.Schema>,
): void => {
  // If this element is a QUESTION type, add its schema
  if (
    element.type === KYCQuestionnaireElementType.QUESTION &&
    element.question
  ) {
    const questionId = element.question.id;
    schemaShape[questionId] = skipWhenHidden(
      questionId,
      createQuestionSchema(element.question),
    );
  }

  // Recursively process nested elements
  if (element.elements && element.elements.length > 0) {
    for (const nestedElement of element.elements) {
      processQuestionnaireElement(nestedElement, schemaShape);
    }
  }
};

/**
 * Returns the root element of a questionnaire. Accepts either the
 * questionnaire wrapper or the root element itself.
 */
export const getQuestionnaireBody = (
  questionnaire: KYCQuestionnaire | KYCQuestionnaireElement,
): KYCQuestionnaireElement | null | undefined =>
  'body' in questionnaire ? questionnaire.body : questionnaire;

/**
 * Creates a complete Yup validation schema from a backend questionnaire.
 *
 * This function traverses the entire questionnaire structure and generates
 * a comprehensive Yup schema that validates all questions, including:
 * - Simple field validations (text, dates, dropdowns, etc.)
 * - Group/array validations with DISTINCT and MAX_SUM rules
 * - Cross-field date dependencies
 * - Nested question structures
 *
 * Trigger visibility comes from the yup context (QuestionnaireValidationContext):
 * questions listed in `hiddenQuestionIDs` skip all their rules, and
 * `answeredOptions` drives the triggered questions inside groups. Forms should
 * use createQuestionnaireResolver, which derives that context from the form
 * values on every validation run.
 *
 * @param questionnaire - The KYCQuestionnaire from the backend
 * @returns A complete Yup object schema for the entire form
 *
 * @example
 * ```ts
 * const schema = createFormSchemaFromQuestionnaire(questionnaire);
 *
 * await schema.validate(values, {
 *   context: { answeredOptions, hiddenQuestionIDs },
 * });
 * ```
 */
export const createFormSchemaFromQuestionnaire = (
  questionnaire: KYCQuestionnaire | KYCQuestionnaireElement,
): yup.ObjectSchema<Record<string, unknown>> => {
  const schemaShape: Record<string, yup.Schema> = {};

  const body = getQuestionnaireBody(questionnaire);

  if (!body) {
    // Return empty schema if no body
    return yup.object({});
  }

  // Process the root element and all its children
  processQuestionnaireElement(body, schemaShape);

  return yup.object(schemaShape);
};
